import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ErrorCode, loginRequest, type LoginRequest } from '@kreart/shared';
import { makeErrorKeyResolver } from '@/lib/form/errorMessages';
import { authStore } from '@/features/auth/store';
import { authApi } from '@/features/auth/api';
import { Button } from '@/components/ui/button';
import { Form, TextField, PasswordField, FormErrorBanner } from '@/components/form';

/**
 * Only the two credential outcomes are worded for this page; everything else —
 * an unreachable API, a rate limit, a server fault — goes through the shared
 * resolver, which names the actual problem. Failing to sign in is exactly when
 * a bare "something went wrong" is least useful.
 */
const resolveLoginError = makeErrorKeyResolver({
  [ErrorCode.AUTH_INVALID_CREDENTIALS]: 'auth.loginError',
  [ErrorCode.AUTH_ACCOUNT_LOCKED]: 'auth.accountLocked',
});

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  const form = useForm<LoginRequest>({
    resolver: zodResolver(loginRequest),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginRequest) => {
    setSubmitError(null);
    setPending(true);
    try {
      const res = await authApi.login(values);
      authStore.setAuthenticated(res.user, res.accessToken, res.accessTokenExpiresAt);
      const target = res.user.mustChangePassword ? '/change-password' : (search.redirect ?? '/');
      await navigate({ to: target });
    } catch (err) {
      setSubmitError(err);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="min-h-full flex items-center justify-center bg-muted/30 px-4 py-12">
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 shadow-sm">
        <h1 className="mb-6 text-center text-xl font-semibold text-foreground">
          {t('auth.loginTitle')}
        </h1>
        <Form form={form} onSubmit={onSubmit} className="space-y-4">
          <TextField
            name="email"
            type="email"
            autoComplete="username"
            label={t('auth.emailLabel')}
          />
          <PasswordField
            name="password"
            autoComplete="current-password"
            label={t('auth.passwordLabel')}
          />
          <FormErrorBanner error={submitError} resolveKey={resolveLoginError} />
          <Button type="submit" className="w-full" disabled={pending}>
            {pending ? t('common.loading') : t('auth.loginButton')}
          </Button>
        </Form>
      </div>
    </div>
  );
}
